import AttendanceIcon from '@material-ui/icons/LibraryAddCheck'
import ParentIcon from '@material-ui/icons/Dashboard'
import ChecklistIcon from '@material-ui/icons/PlaylistAddCheck'
import VisitorIcon from '@material-ui/icons/AssignmentInd'
import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import styled, { css } from 'styled-components'

import { navigationPaths } from 'app/config'
import { colors } from 'app/theme/colors'
import { mobile, shadow, space, zIndex } from 'app/theme/tokens'

/**
 * Bottom navigation bar — visible only on mobile. Four fixed slots, each
 * taking 25% of the width: the daily-use surfaces a centre-staff / front-desk
 * user reaches for from their phone.
 *
 *   [ Attendance ]  [ Parent ]  [ Checklist ]  [ Visitor ]
 *
 * Other features remain reachable through the hamburger drawer.
 *
 * Safe-area aware: reserves extra padding for the iOS home-indicator strip
 * via env(safe-area-inset-bottom).
 *
 * Pair with a bottom-padding of `mobile.bottomNavHeight + safe-area-bottom`
 * on the main content so children aren't covered.
 */

// The four bottom-nav slots. Edit here if priorities change — single source
// of truth so D7 / D8 always agree on what counts as "priority".
export const MOBILE_PRIMARY_ROUTES = [
  {
    key: 'attendance',
    label: 'Attendance',
    path: '/classes/attendance/mark',
    Icon: AttendanceIcon
  },
  {
    key: 'parent',
    label: 'Parent',
    path: '/home/parent',
    Icon: ParentIcon
  },
  {
    key: 'checklists',
    label: 'Checklist',
    path: '/operations/checklists',
    Icon: ChecklistIcon
  },
  {
    key: 'visitor',
    label: 'Visitor',
    path: '/operations/visitor-entry',
    Icon: VisitorIcon
  }
]

// Convenience set used by the desktop-fallback component to decide which
// pages it should NOT show the "best on desktop" splash on. Inventory booking
// lost its bar slot to the checklist but stays mobile-ready, so it keeps its
// splash exemption.
export const MOBILE_PRIMARY_PATHS = new Set(
  [...MOBILE_PRIMARY_ROUTES.map((r) => r.path), '/operations/inventory']
)

const Bar = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: calc(${mobile.bottomNavHeight} + ${mobile.safeAreaBottom});
  padding-bottom: ${mobile.safeAreaBottom};
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  background: ${colors.white};
  border-top: 1px solid ${colors.borderDefault};
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
  z-index: ${zIndex.sticky};
`

const Slot = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-height: ${mobile.tapTarget};
  text-decoration: none;
  color: ${colors.textSecondary};

  &:hover, &:focus {
    color: ${colors.ohOrange};
    background: ${colors.ohLightOrange};
    text-decoration: none;
  }

  ${(props) =>
    props.$selected &&
    css`
      color: ${colors.ohOrange};
      font-weight: 600;
    `}
`

const SlotLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.02em;
  text-transform: lowercase;
`

const MobileBottomNav = () => {
  const { pathname } = useLocation()

  return (
    <Bar>
      {MOBILE_PRIMARY_ROUTES.map(({ key, label, path, Icon }) => {
        const selected = pathname === path || pathname.startsWith(path + '/')
        return (
          <Slot key={key} to={path} $selected={selected}>
            <Icon fontSize="small" />
            <SlotLabel>{label}</SlotLabel>
          </Slot>
        )
      })}
    </Bar>
  )
}

export default MobileBottomNav
